/* eslint-disable no-param-reassign, sonarjs/cognitive-complexity */

import debounce from '../utils/debounce';

export default class Platforms {
  constructor() {
    this.selector = '.b-platforms';

    this.selectors = {
      filtersForm: '.b-platforms__filters-form',
      resetButton: '.b-platforms__btn-reset',
      items: '.b-platforms__items',
      platformCard: '.c-platform-card',
      platformCardVisible: '.c-platform-card:not(.c-platform-card--hidden)',
      sortBy: '.b-platforms__sort-by',
      resultsCount: '.b-platforms__results-count',
      filtersInnerWrap: '.b-platforms__filters-inner-wrap',
      streams: '.c-platform-card__meta-description--streams',
    };

    this.classes = {
      inputRange: 'c-range__slider',
      platformCardHidden: 'c-platform-card--hidden',
      sortByActive: 'b-platforms__sort-by--active',
    };

    this.nodes = {};
    this.items = [];
    this.filteredItems = [];
  }

  bootstrap() {
    const block = document.querySelector(this.selector);

    if (!block) {
      return;
    }

    this.block = block;

    this.getNodes({ block });

    this.updateStreams();
    this.adjustFilterStickness();
    window.addEventListener('resize', () => this.adjustFilterStickness());

    const filterInputs = [ ...this.nodes.filtersForm.querySelectorAll('input') ];

    filterInputs.forEach(filterInput => {
      filterInput.addEventListener(
        filterInput.classList.contains(this.classes.inputRange) ?
          'c-range:change' :
          'input',
        debounce(this.onFilterInput.bind(this), 200),
      );
    });

    this.handleSorting();

    this.nodes.resetButton.addEventListener('click', event => {
      event.preventDefault();
      this.nodes.filtersForm.reset();

      const inputEvent = new Event('input', {
        bubbles: true,
        cancelable: true,
      });

      filterInputs.forEach(item => item.dispatchEvent(inputEvent));

      setTimeout(() => {
        const formData = new FormData(this.nodes.filtersForm);

        this.filterItems(formData, true);
      }, 200);
    });
  }

  adjustFilterStickness() {
    const { filtersInnerWrap } = this.nodes;
    let delta = window.innerHeight - filtersInnerWrap.offsetHeight || 0;

    delta = delta < 0 ? delta : 0;

    const cssVarName = '--b-platforms__filters-y-diff';

    if (delta) {
      filtersInnerWrap.style.setProperty(cssVarName, `${delta}px`);
    } else {
      filtersInnerWrap.style.removeProperty(cssVarName);
    }
  }

  handleSorting() {
    const { sortBy } = this.nodes;

    sortBy.forEach(sortItem => {
      sortItem.addEventListener('click', () => {
        const wasActive = sortItem.classList.contains(
          this.classes.sortByActive,
        );

        if (wasActive) {
          // eslint-disable-next-line no-param-reassign
          sortItem.dataset.order =
            sortItem.dataset.order === 'desc' ? 'asc' : 'desc';
        }

        sortBy.forEach(sortItemToRemoveActive =>
          sortItemToRemoveActive.classList.remove(this.classes.sortByActive),);
        sortItem.classList.add(this.classes.sortByActive);

        this.sortByProperty(sortItem.dataset.prop, sortItem.dataset.order);
      });
    });
  }

  sortByProperty(orderBy = 'vendor', order = 'asc') {
    const items = [ ...this.block.querySelectorAll(this.selectors.platformCard) ];

    items.sort((itemA, itemB) => {
      const valueA = itemA.dataset[orderBy].toLowerCase();
      const valueB = itemB.dataset[orderBy].toLowerCase();
      const dirValue = order === 'desc' ? -1 : 1;

      return valueA < valueB ? -1 * dirValue : dirValue;
    });

    const itemsContainer = this.nodes.items;

    itemsContainer.innerHTML = '';

    items.forEach(item => {
      itemsContainer.appendChild(item);
    });
  }

  getFormValues() {
    const formData = new FormData(this.nodes.filtersForm);

    [ ...this.nodes.filtersForm.querySelectorAll('.c-range__slider') ].forEach(
      input => {
        formData.set(
          input.name,
          input.dataset.rangeValue || input.dataset.value || 0,
        );
      },
    );

    return formData;
  }

  onFilterInput() {
    this.filterItems(this.getFormValues());
  }

  updateStreams() {
    const items = [ ...this.block.querySelectorAll(this.selectors.platformCard) ];

    const formData = this.getFormValues();

    const fps = parseInt(formData.get('fps'), 10) || 0;

    items.forEach(item => {
      const itemFps = parseInt(item.dataset.fps, 10) || 0;
      const itemExpectedFps = parseInt(item.dataset.expectedFps, 10) || 0;
      const streams1 = Math.floor(itemExpectedFps / fps);
      const streams2 = Math.floor(itemFps / fps);

      item.querySelector(this.selectors.streams).innerHTML = Math.min(
        streams1,
        streams2,
      );
    });
  }

  filterItems(formData, isReset = false) {
    const items = [ ...this.block.querySelectorAll(this.selectors.platformCard) ];

    items.forEach(item => {
      item.classList.remove(this.classes.platformCardHidden);
    });

    if (isReset) {
      return;
    }

    const fps = parseInt(formData.get('fps'), 10) || 0;
    const stream = parseInt(formData.get('streams'), 10) || 0;
    const tops = parseInt(formData.get('tops'), 10) || 0;
    const selectedArchitectures = [ ...formData.getAll('architecture') ];
    const selectedCooling = [ ...formData.getAll('cooling') ];

    this.updateStreams();

    items.forEach(item => {
      const itemFps = parseInt(item.dataset.fps, 10) || 0;
      const itemExpectedFps = parseInt(item.dataset.expectedFps, 10) || 0;
      const itemTops = parseInt(item.dataset.tops, 10) || 0;

      const res = [
        (parseInt(itemExpectedFps / fps, 10) || 0) >= stream ||
          !itemExpectedFps,
        (parseInt(itemFps / fps, 10) || 0) >= stream,
        itemTops >= tops,
        selectedArchitectures.length === 0 ||
          selectedArchitectures.includes(item.dataset.architecture || ''),
        selectedCooling.length === 0 ||
          selectedCooling.includes(item.dataset.cooling || ''),
      ].every(condition => Boolean(condition));

      if (!res) {
        item.classList.add(this.classes.platformCardHidden);
      }
    });

    const itemsVisibleNumber = this.block.querySelectorAll(
      this.selectors.platformCardVisible,
    ).length;

    this.nodes.resultsCount.innerHTML = `Found ${itemsVisibleNumber} Results`;
  }

  getNodes({ block }) {
    this.nodes = {
      filtersForm: block.querySelector(this.selectors.filtersForm),
      items: block.querySelector(this.selectors.items),
      sortBy: [ ...block.querySelectorAll(this.selectors.sortBy) ],
      resultsCount: block.querySelector(this.selectors.resultsCount),
      resetButton: block.querySelector(this.selectors.resetButton),
      filtersInnerWrap: block.querySelector(this.selectors.filtersInnerWrap),
    };
  }
}
